import { type AppNode } from '../../types';
import { type ClipboardItem } from './types';

export type PasteGroup = {
  /** Indices into the returned nodes array for members of this group */
  memberIndices: number[];
};

export type PasteResult = {
  nodes: AppNode[];
  /** Groups to recreate on the backend after paste. Only includes groups with 2+ members. */
  groups: PasteGroup[];
};

const PASTE_OFFSET = 24;

/**
 * Computes the bounding box of a set of objects
 */
export function computeBoundingBox(objects: AppNode[]): ClipboardItem['boundingBox'] {
  if (objects.length === 0) {
    return { maxX: 0, maxY: 0, minX: 0, minY: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const obj of objects) {
    const width = obj.measured?.width ?? obj.width ?? 0;
    const height = obj.measured?.height ?? obj.height ?? 0;

    minX = Math.min(minX, obj.position.x);
    minY = Math.min(minY, obj.position.y);
    maxX = Math.max(maxX, obj.position.x + width);
    maxY = Math.max(maxY, obj.position.y + height);
  }

  return { maxX, maxY, minX, minY };
}

/**
 * Prepares objects for pasting with new IDs and adjusted positions.
 * Clears groupId so pasted nodes start ungrouped, and returns group
 * membership info so callers can recreate groups on the backend.
 */
export function prepareObjectsForPaste(
  clipboardItem: ClipboardItem,
  pastePosition: { x: number; y: number },
  pasteCount = 0,
): PasteResult {
  const diagonalOffset = PASTE_OFFSET * (pasteCount + 1);

  const groupIdToIndices = new Map<string, number[]>();

  for (let i = 0; i < clipboardItem.nodes.length; i++) {
    const groupId = clipboardItem.nodes[i].groupId;

    if (groupId) {
      const existing = groupIdToIndices.get(groupId);

      if (existing) {
        existing.push(i);
      } else {
        groupIdToIndices.set(groupId, [i]);
      }
    }
  }

  // When the clipboard includes a multi-member group, place the whole paste
  // below the source bounding box (y only) to avoid overlap with the original
  // group (which would trigger useSelectionChange's auto-expand). Use the same
  // strategy for every node so mixed grouped/ungrouped selections keep their
  // spatial layout. Otherwise use a diagonal offset for repeat pastes.
  const { boundingBox } = clipboardItem;
  const boundsHeight = boundingBox.maxY - boundingBox.minY;
  const belowOffset = (boundsHeight + PASTE_OFFSET) * (pasteCount + 1);
  const pasteUsesBelowOffset = [...groupIdToIndices.values()].some((indices) => indices.length >= 2);

  const nodes = clipboardItem.nodes.map((obj) => {
    const newId = crypto.randomUUID();

    // Calculate new position relative to paste position
    const relativeX = obj.position.x - boundingBox.minX;
    const relativeY = obj.position.y - boundingBox.minY;

    const newPosition = pasteUsesBelowOffset
      ? { x: pastePosition.x + relativeX, y: pastePosition.y + relativeY + belowOffset }
      : { x: pastePosition.x + relativeX + diagonalOffset, y: pastePosition.y + relativeY + diagonalOffset };

    // Clone the object
    const clonedObj = structuredClone(obj);
    clonedObj.id = newId;
    clonedObj.remoteId = undefined;
    clonedObj.groupId = undefined;
    clonedObj.position = newPosition;
    clonedObj.selected = true;

    // Clear task and processing state
    if ('task' in clonedObj.data) {
      clonedObj.data.task = undefined;
    }

    // Clear processing state for images
    if (clonedObj.type === 'image') {
      clonedObj.data.isProcessing = false;
    }

    // Clear editing state for text nodes
    if (clonedObj.type === 'text') {
      clonedObj.data.isEditing = false;
    }

    return clonedObj;
  });

  // The backend requires at least 2 members to form a group
  const groups: PasteGroup[] = [];

  for (const [, indices] of groupIdToIndices) {
    if (indices.length >= 2) {
      groups.push({ memberIndices: indices });
    }
  }

  return { groups, nodes };
}
